export const TOPIC_STATUSES = [
  "待确认",
  "已确认",
  "制作中",
  "待审阅",
  "待发布",
  "已完成",
  "不做",
] as const;

export type TopicStatus = (typeof TOPIC_STATUSES)[number];

export const BOARD_STATUSES = ["已确认", "制作中", "待审阅", "待发布", "已完成"] as const;

export type BoardStatus = (typeof BOARD_STATUSES)[number];

export const PLATFORM_IDS = ["xiaohongshu", "douyin", "x", "instagram"] as const;

export type PlatformId = (typeof PLATFORM_IDS)[number];

/** Built-in workspace id. Added accounts use this id or their own id. */
export type AccountId = string;

export const TASK_TYPES = [
  "找选题",
  "生成标题",
  "生成文案",
  "生成图文帖子",
  "生成口播稿",
  "同步账号数据",
  "找对标爆款",
  "拆解对标",
  "网感打分",
  "按建议重写",
] as const;

export type TaskType = (typeof TASK_TYPES)[number];

export const TASK_STATUSES = ["排队", "进行中", "已完成", "失败"] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Platforms a find-topics task can target. TikTok stays distinct from the 抖音 workspace. */
export const DISTRIBUTION_PLATFORMS = ["X", "Instagram", "Facebook", "TikTok", "小红书", "抖音"] as const;

export type DistributionPlatform = (typeof DISTRIBUTION_PLATFORMS)[number];

export interface Topic {
  id: string;
  title: string;
  status: TopicStatus;
  batch: string;
  angle: string;
  audience: string;
  sources: string;
  createdDate: string | null;
  notionUrl: string;
  publishUrl: string | null;
  plannedPublishDate: string | null;
  /** Raw Notion date start for 计划发布时间. May include a time. */
  plannedPublishAt: string | null;
  /** Raw Notion date start for 实际发布时间. */
  actualPublishAt: string | null;
  sourceAssistant: string;
  reason: string;
  heat: number | null;
  heatBasis: string;
  referenceUrl: string | null;
  queueOrder: number | null;
  /** Workspace this topic belongs to. Missing values are treated as 小红书. */
  accountId: AccountId;
  recommendRank: number | null;
  viralReason: string;
  sourceLinks: string[];
  sourcePlatform: string;
  sourceTaskId: string | null;
  platforms: string[];
  notes: string;
  notesUpdatedAt: string | null;
  lastEditedTime: string;
  updatedAt: string;
  inTrash: boolean;
  syncError: string | null;
}

export interface Activity {
  id: string;
  topicId: string;
  type: "status_change" | "sync_conflict";
  fromStatus: TopicStatus | null;
  toStatus: TopicStatus | null;
  message: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AccountSnapshot {
  id: string;
  platform: PlatformId;
  recordedDate: string;
  followers: number | null;
  posts: number | null;
  views: number | null;
  likes: number | null;
  saves: number | null;
  comments: number | null;
  updatedAt: string;
  deletedAt: string | null;
}

export interface PostMetric {
  id: string;
  platform: PlatformId;
  topicId: string | null;
  title: string;
  url: string;
  recordedDate: string;
  views: number | null;
  likes: number | null;
  saves: number | null;
  comments: number | null;
  updatedAt: string;
  deletedAt: string | null;
}

export interface NotionSchemaInfo {
  dataSourceId: string | null;
  statusType: "select" | "status" | null;
  publishUrl: boolean;
  plannedDate: boolean;
  platforms: boolean;
  notes: boolean;
  missingRequired: string[];
}

export interface NotionSyncInfo {
  lastSyncAt: string | null;
  lastError: string | null;
  schema: NotionSchemaInfo | null;
}

export interface StatusOutbox {
  id: string;
  kind: "set_status";
  topicId: string;
  status: TopicStatus;
  baseLastEditedTime: string;
  activityId: string;
  fromStatus: TopicStatus;
  updatedAt: string;
  lastError: string | null;
}

export interface FieldOutbox {
  id: string;
  kind: "patch_fields";
  topicId: string;
  baseLastEditedTime: string;
  updatedAt: string;
  lastError: string | null;
  publishUrl?: string | null;
  plannedPublishDate?: string | null;
  platforms?: string[];
  notes?: string;
  queueOrder?: number | null;
}

export type OutboxItem = StatusOutbox | FieldOutbox;

/** Share of a find-topics search. Selected platforms sum to 100. */
export interface PlatformWeight {
  platform: string;
  weight: number;
}

export interface AgentTask {
  id: string;
  type: TaskType;
  accountId: AccountId;
  platforms: string[];
  /** How the assistant should split searching and ranking. Empty means an even split. */
  weights: PlatformWeight[];
  direction: string;
  count: number;
  topicId: string | null;
  /** Benchmark note id for 拆解对标. Other task types leave this empty. */
  subjectId: string | null;
  /** Executor id that claimed this task. Empty when a workspace secret claimed it, or nobody has. */
  claimedBy: string | null;
  status: TaskStatus;
  result: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
}

export interface AccountProfile {
  id: string;
  platform: string;
  displayName: string;
  profileUrl: string;
  note: string;
  /** Set when the person added the account. Empty means a leftover placeholder. */
  addedAt: string | null;
  updatedAt: string;
}

/** A reference viral note. Counts are what was seen on recordedDate, not a live feed. */
export interface BenchmarkNote {
  id: string;
  accountId: AccountId;
  link: string;
  platform: string;
  title: string;
  author: string;
  likes: number | null;
  collects: number | null;
  comments: number | null;
  recordedDate: string;
  coverUrl: string;
  note: string;
  titlePattern: string;
  coverType: string;
  bodyStructure: string;
  aiTools: string;
  interaction: string;
  whyViral: string;
  sourceTaskId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface StyleGuideVersion {
  id: string;
  accountId: AccountId;
  titleFormula: string;
  coverLayout: string;
  voice: string;
  aiPractice: string;
  banned: string;
  goodTitles: string;
  badTitles: string;
  createdAt: string;
  updatedAt: string;
  current: boolean;
}

export interface PostScore {
  id: string;
  accountId: AccountId;
  topicId: string;
  styleVersionId: string | null;
  sense: number;
  ai: number;
  saveValue: number;
  cover: number;
  senseReason: string;
  aiReason: string;
  saveReason: string;
  coverReason: string;
  createdAt: string;
  updatedAt: string;
}

/** Public executor fields. The API key and wake header value never leave the server. */
export interface ExecutorProfile {
  id: string;
  name: string;
  wakeUrl: string;
  headerName: string;
  headerSet: boolean;
  taskTypes: TaskType[];
  keyHint: string;
  enabled: boolean;
  lastWakeAt: string | null;
  lastWakeStatus: number | null;
  lastWakeError: string;
  lastClaimAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** id matches accountId so sync can merge one row per workspace. */
export interface ScoreSetting {
  id: string;
  accountId: AccountId;
  passTotal: number;
  updatedAt: string;
}

export interface LocalState {
  topics: Topic[];
  activities: Activity[];
  snapshots: AccountSnapshot[];
  postMetrics: PostMetric[];
  tasks: AgentTask[];
  accounts: AccountProfile[];
  benchmarks: BenchmarkNote[];
  styleGuides: StyleGuideVersion[];
  postScores: PostScore[];
  scoreSettings: ScoreSetting[];
  executors: ExecutorProfile[];
  outbox: OutboxItem[];
}

export interface SyncResultItem {
  outboxId: string;
  ok: boolean;
  conflict?: boolean;
  error?: string;
  /** updatedAt of the outbox item the server actually applied. */
  updatedAt?: string;
}

export interface SyncResponse {
  serverTime: string;
  topics: Topic[];
  activities: Activity[];
  snapshots: AccountSnapshot[];
  postMetrics: PostMetric[];
  tasks?: AgentTask[];
  accounts?: AccountProfile[];
  benchmarks?: BenchmarkNote[];
  styleGuides?: StyleGuideVersion[];
  postScores?: PostScore[];
  scoreSettings?: ScoreSetting[];
  executors?: ExecutorProfile[];
  notion: NotionSyncInfo;
  results: SyncResultItem[];
  /** True only when the worker has AGENT_SECRET. Omitted means not connected. */
  agentConfigured?: boolean;
}

export interface SyncRequest {
  forceNotion: boolean;
  outbox: OutboxItem[];
  activities: Activity[];
  snapshots: AccountSnapshot[];
  postMetrics: PostMetric[];
  tasks?: AgentTask[];
  accounts?: AccountProfile[];
  benchmarks?: BenchmarkNote[];
  styleGuides?: StyleGuideVersion[];
  postScores?: PostScore[];
  scoreSettings?: ScoreSetting[];
}
